import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import dayjs from 'dayjs';
import { useAppContext } from 'providers/AppProvider';
import { TooltipComponent } from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { tooltipFormatterList } from 'helpers/echart-utils';

// Definir la interfaz en el mismo archivo
interface KPIData {
  date: string;
  count: number;
}

interface KPIEvolutionChartProps {
  data: KPIData[];
}

echarts.use([TooltipComponent, BarChart]);

const getDefaultOptions = (
  getThemeColor: (name: string) => string,
  isDark: boolean,
  data: { date: string; count: number }[]
) => ({
  tooltip: {
    trigger: 'axis',
    padding: 10,
    // eslint-disable-next-line
    backgroundColor: !isDark? 'rgba(240, 240, 240, 0.9)' : 'rgba(49, 55, 74, 0.9)',
    textStyle: {
      color: !isDark ? '#202020' : '#ccc',
      fontFamily: 'Nunito Sans',
      fontSize: 14
    },
    borderWidth: 1,
    // eslint-disable-next-line
    borderColor: !isDark ? 'rgba(49, 55, 74, 0.4)' : 'rgba(140, 140, 180, 0.8)',
    transitionDuration: 0,
    axisPointer: {
      type: 'none'
    },
    formatter: tooltipFormatterList
  },
  xAxis: [
    {
      type: 'category',
      data: data.map(item => item.date),
      show: true,
      boundaryGap: false,
      axisLine: {
        show: false,
        lineStyle: { color: getThemeColor('secondary-bg') }
      },
      axisTick: {
        show: false
      },
      axisLabel: {
        formatter: (value: Date) => dayjs(value).format('MMM DD'),
        showMinLabel: true,
        showMaxLabel: true,
        color: getThemeColor('secondary-color'),
        align: 'left',
        interval: 5,
        fontFamily: 'Nunito Sans',
        fontSize: 10
      }
    },
    {
      type: 'category',
      position: 'bottom',
      show: true,
      data: data.map(item => item.date),
      axisLabel: {
        formatter: (value: Date) => dayjs(value).format('MMM DD'),
        interval: 130,
        showMaxLabel: true,
        showMinLabel: false,
        color: getThemeColor('secondary-color'),
        align: 'right',
        fontFamily: 'Nunito Sans',
        fontSize: 10
      },
      axisLine: {
        show: false
      },
      axisTick: {
        show: false
      },
      splitLine: {
        show: false
      },
      boundaryGap: false
    }
  ],
  yAxis: {
    show: false,
    scale: 'true',
    type: 'value',
    boundaryGap: false
  },
  series: [
    {
      type: 'line',
      smooth: true,
      data: data.map(item => item.count),
      lineStyle: {
        width: 2,
        color: getThemeColor('primary')
      },
      showSymbol: false,
      symbol: 'circle',
      itemStyle: {
        color: getThemeColor('primary')
      }
    }
  ],
  grid: { left: 0, right: 0, top: 5, bottom: 27 }
});

const KPIEvolutionChart = ({ data }: KPIEvolutionChartProps) => {
  const {
    getThemeColor,
    config: { isDark }
  } = useAppContext();

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={getDefaultOptions(getThemeColor, isDark, data)}
      style={{ height: '80px', width: '100%' }}
    />
  );
};

export default KPIEvolutionChart;
